import {
  CHARACTERS_URL, resolveClip, wornParts, type CharacterAnimation, type CharacterOutfit, type CharacterDesign, type CharacterDesignInput, type CharacterFrame,
} from '../../shared/characters.ts';
import type { Direction } from '../../shared/protocol.ts';
import { assetImage } from './element-renderer.ts';

/** Hauteur des pieds dans la case (comme le personnage dessiné par défaut). */
const FEET = 0.94;

type SheetSource = Pick<CharacterDesignInput, 'image' | 'transparentColor' | 'tolerance'>;

const sheets = new Map<string, HTMLCanvasElement>();

/** Oublie les copies préparées d'une planche (son image a été remplacée). */
export function forgetCharacterSheet(image: string): void {
  for (const key of [...sheets.keys()]) if (key.startsWith(`${image}|`)) sheets.delete(key);
}

/**
 * La planche prête à dessiner : une copie dont la couleur de fond est rendue transparente
 * (null tant que l'image charge). Gardée en cache par image, couleur et tolérance.
 */
export function characterSheet({ image, transparentColor, tolerance }: SheetSource): HTMLCanvasElement | null {
  const key = `${image}|${transparentColor ?? ''}|${tolerance}`;
  const cached = sheets.get(key);
  if (cached) return cached;
  const source = assetImage(CHARACTERS_URL, image);
  if (!source.complete || !source.naturalWidth) return null;
  const canvas = document.createElement('canvas');
  canvas.width = source.naturalWidth;
  canvas.height = source.naturalHeight;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(source, 0, 0);
  if (transparentColor) {
    const [r, g, b] = [1, 3, 5].map((i) => parseInt(transparentColor.slice(i, i + 2), 16)) as [number, number, number];
    const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const { data } = pixels;
    for (let i = 0; i < data.length; i += 4) {
      if (Math.abs(data[i]! - r) <= tolerance && Math.abs(data[i + 1]! - g) <= tolerance && Math.abs(data[i + 2]! - b) <= tolerance) data[i + 3] = 0;
    }
    ctx.putImageData(pixels, 0, 0);
  }
  sheets.set(key, canvas);
  return canvas;
}

/** La planche multipliée par `color` (blanc → la couleur, gris → plus sombre), transparence d'origine gardée. */
function tintedSheet(source: SheetSource, color: string): HTMLCanvasElement | null {
  const key = `${source.image}|${source.transparentColor ?? ''}|${source.tolerance}|${color}`;
  const cached = sheets.get(key);
  if (cached) return cached;
  const base = characterSheet(source);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!base || !ctx) return null;
  canvas.width = base.width;
  canvas.height = base.height;
  ctx.drawImage(base, 0, 0);
  ctx.globalCompositeOperation = 'multiply';
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.globalCompositeOperation = 'destination-in';
  ctx.drawImage(base, 0, 0);
  sheets.set(key, canvas);
  return canvas;
}

/**
 * Planches à superposer, aux mêmes coordonnées : le corps, puis les pièces portées (la tenue donnée, sinon celle
 * par défaut de l'apparence) dans l'ordre des emplacements, teintées au besoin ; une pièce qui charge encore est
 * sautée. Null tant que le corps charge. Les pièces ont leur propre transparence : le fond du corps ne s'y applique pas.
 */
export function characterLayers(design: CharacterDesignInput, outfit: CharacterOutfit | null = null): HTMLCanvasElement[] | null {
  const body = characterSheet(design);
  if (!body) return null;
  const layers = [body];
  for (const { part, color } of wornParts(design, outfit)) {
    const source = { image: part.image, transparentColor: null, tolerance: 0 };
    const sheet = color ? tintedSheet(source, color) : characterSheet(source);
    if (sheet) layers.push(sheet);
  }
  return layers;
}

/** Part d'une ligne (ou colonne) opaque au-delà de laquelle c'est un trait de grille de la planche. */
const GRID_LINE_FILL = 0.9;

/**
 * Plus petit rectangle non transparent contenu dans `frame` (null s'il est vide). Les lignes et colonnes
 * opaques presque d'un bord à l'autre sont des traits de la grille dessinée sur la planche : ignorées.
 */
export function opaqueBounds(sheet: HTMLCanvasElement, frame: CharacterFrame): CharacterFrame | null {
  const ctx = sheet.getContext('2d', { willReadFrequently: true });
  if (!ctx || frame.w < 1 || frame.h < 1) return null;
  const { data } = ctx.getImageData(frame.x, frame.y, frame.w, frame.h);
  const opaque = (x: number, y: number) => data[(y * frame.w + x) * 4 + 3]! >= 16;
  const rows = new Array<number>(frame.h).fill(0), columns = new Array<number>(frame.w).fill(0);
  for (let y = 0; y < frame.h; y++) {
    for (let x = 0; x < frame.w; x++) {
      if (!opaque(x, y)) continue;
      rows[y]!++;
      columns[x]!++;
    }
  }
  const lineRow = (y: number) => frame.w > 8 && rows[y]! >= frame.w * GRID_LINE_FILL;
  const lineColumn = (x: number) => frame.h > 8 && columns[x]! >= frame.h * GRID_LINE_FILL;
  let minX = frame.w, minY = frame.h, maxX = -1, maxY = -1;
  for (let y = 0; y < frame.h; y++) {
    if (lineRow(y)) continue;
    for (let x = 0; x < frame.w; x++) {
      if (lineColumn(x) || !opaque(x, y)) continue;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }
  return maxX < 0 ? null : { x: frame.x + minX, y: frame.y + minY, w: maxX - minX + 1, h: maxY - minY + 1 };
}

/** Couleur #rrggbb d'un pixel de l'image d'origine (pipette de la couleur transparente). */
export function pixelColor(image: string, x: number, y: number): string | null {
  const sheet = characterSheet({ image, transparentColor: null, tolerance: 0 });
  const ctx = sheet?.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  const [r, g, b] = ctx.getImageData(x, y, 1, 1).data;
  return `#${[r, g, b].map((value) => (value ?? 0).toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Dessine une image de planche, pieds (bas du rectangle moins la marge) sur le point (cx, feetY).
 * Renvoie le haut de l'image dessinée (null si la planche n'est pas encore chargée).
 */
export function drawCharacterFrame(
  ctx: CanvasRenderingContext2D, design: CharacterDesignInput, frame: CharacterFrame, mirror: boolean, cx: number, feetY: number, size: number,
  outfit: CharacterOutfit | null = null,
): number | null {
  const layers = characterLayers(design, outfit);
  if (!layers) return null;
  const scale = size / design.pixelsPerTile;
  const w = frame.w * scale, h = frame.h * scale;
  const top = feetY - (frame.h - design.footOffset) * scale;
  ctx.save();
  // Pixel-art agrandi : net ; grande planche réduite : lissée pour éviter le crénelage.
  ctx.imageSmoothingEnabled = scale < 1;
  ctx.imageSmoothingQuality = 'high';
  ctx.translate(Math.round(cx), 0);
  if (mirror) ctx.scale(-1, 1);
  for (const sheet of layers) ctx.drawImage(sheet, frame.x, frame.y, frame.w, frame.h, -w / 2, top, w, h);
  ctx.restore();
  return top;
}

/**
 * Personnage dans la case (px, py) de taille `size` : l'animation demandée (ou celle qui la remplace),
 * à l'instant `elapsed` (ms depuis le début de l'animation), avec sa tenue (apparence personnalisable). Renvoie le haut du dessin, ou null si
 * rien n'a pu être dessiné (aucune image découpée, planche en cours de chargement).
 */
export function drawCharacter(
  ctx: CanvasRenderingContext2D, design: CharacterDesign | CharacterDesignInput, animation: CharacterAnimation, dir: Direction,
  elapsed: number, px: number, py: number, size: number, outfit: CharacterOutfit | null = null,
): number | null {
  const resolved = resolveClip(design.animations, animation, dir);
  if (!resolved) return null;
  const { frames, ms } = resolved.clip;
  const frame = frames[Math.floor(Math.max(0, elapsed) / ms) % frames.length];
  return frame ? drawCharacterFrame(ctx, design, frame, resolved.mirror, px + size / 2, py + size * FEET, size, outfit) : null;
}
